import { v4 as uuid } from 'uuid';
import compact from 'lodash/compact';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';

import {
    EntryTypeFieldType,
    RenderedServiceCreateForm,
    RenderedServiceData,
    RenderedServiceUpdateForm,
} from 'chaika-frontend-types/frontend-cms';
import { RenderedServiceStatus } from 'chaika-enums';

import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { RenderedServiceList } from '@store/models/SystemFields/RenderedService';
import { userQueryOptions } from '@user/queries/userQueries';
import { isAssistant } from '@user/utils/isAssistant';
import { isDoctor } from '@user/utils/isDoctor';
import { serviceService } from '@services/services/serviceService';
import { EntryApi } from '@api/EntryApi';
import { PlanApi } from '@api/PlanApi';
import { planService } from '@plan/service/planService';
import { RenderedServicePlanParams, RenderedServiceUpdateParams } from 'chaika-types';
import { DateHelper } from '@utils/DateHelper';
import { ehrsServicePlansService } from '../ehr/ehrsServicePlansService';
import { RenderedServiceApi } from '@api';
import { serviceInformedConsentStore } from '@patient/ui/shared/ServiceInformedConsent/store/serviceInformedConsentStore';
import { translate } from '@chaika/i18n/helper';
import { removeNilKeys } from '@utils/removeNiKeys';
import { isDefined } from '@utils/isDefined';
import { Money } from '@store/models/Money';

export class RenderedServicesService {
    public static removeNotChanged(
        { plans, coExecutorIds, ...current }: RenderedServiceData,
        updateParams: RenderedServiceUpdateParams,
    ): RenderedServiceUpdateParams {
        const currentRenderedService = {
            ...current,
            coExecutorIds: coExecutorIds ?? [],
            plans: orderBy(
                plans.map(({ data, ...plan }) => ({ ...plan, data: data ?? {} })),
                'id',
            ),
        };
        const updateRenderedService = {
            ...updateParams,
            coExecutorIds: isDefined(updateParams.coExecutorIds)
                ? updateParams.coExecutorIds
                : currentRenderedService.coExecutorIds,
            plans: isDefined(updateParams.plans)
                ? orderBy(
                      updateParams.plans.map(({ data, ...plan }) => ({ ...plan, data: data ?? {} })),
                      'id',
                  )
                : undefined,
        };

        return omitBy(removeNilKeys(updateRenderedService), (value, key) =>
            isEqual(value, currentRenderedService[key as keyof typeof currentRenderedService]),
        );
    }

    public async create(createForm: RenderedServiceCreateForm): Promise<void> {
        const entry = store.entry;
        const data = await PlanApi.getOptimalPlans({
            serviceId: createForm.renderedService.serviceId,
            ehrId: entry.ehrId,
            organizationId: entry.organizationId,
            authorId: entry.authorId,
            date: DateHelper.getStartOfDay().toISOString(),
        });

        const includedServices = data?.includedServices ?? [];
        let planIds: string[] = [];

        if (includedServices.length > 0) {
            const forms = includedServices.map(item => ({
                ids: [uuid()],
                renderedService: {
                    serviceId: item.id,
                    plans: item.plans,
                    contractorId: createForm.renderedService.contractorId,
                },
            }));

            await Promise.all(forms.map(item => this.createRenderedService(item)));
            planIds = forms.flatMap(item => this.getServicePlanIds(item.renderedService));
        } else {
            await this.createRenderedService(createForm);
            planIds = this.getServicePlanIds(createForm.renderedService);
        }

        await Promise.all([planService.loadByIds(planIds), this.updateStore()]);
    }

    public async update(id: string, { renderedService }: RenderedServiceUpdateForm): Promise<void> {
        const renderedServiceList = this.getRenderedServiceList();
        const { ids, ...current } = renderedServiceList.getExisting(id);

        const updateParams = RenderedServicesService.removeNotChanged(current, renderedService);
        const planIds = renderedService.plans?.map(({ id }) => id);

        await Promise.all([
            planService.loadByIds(planIds ?? []),
            EntryApi.updateRenderedService(store.entry.id, { ids, renderedService: updateParams }),
        ]);
    }

    public async changeContractor(ids: string[], contractorId: string): Promise<void> {
        await EntryApi.updateRenderedServiceContractor(store.entry.id, { ids, contractorId });
        await this.updateStore();
    }

    public async add(id: string, addedCount: number): Promise<void> {
        const renderedServiceList = this.getRenderedServiceList();
        const { plans, serviceId, contractorId } = renderedServiceList.getExisting(id);

        const newIds: string[] = [];

        for (let i = 0; i < addedCount; i++) {
            newIds.push(uuid());
        }

        if (newIds.length > 0) {
            await this.createRenderedService({
                ids: newIds,
                renderedService: {
                    serviceId,
                    plans: plans.length > 0 ? (plans as RenderedServicePlanParams[]) : undefined,
                    contractorId,
                },
            });

            renderedServiceList.addItems(id, newIds);
        }
    }

    public async remove(id: string, removingCount?: number): Promise<void> {
        const renderedServiceList = this.getRenderedServiceList();
        const { ids } = renderedServiceList.getExisting(id);

        const removingIds = removingCount ? ids.slice(0, removingCount) : ids;

        if (removingIds.length > 0) {
            await this.removeRenderedServices(id, removingIds);
        }
    }

    public async removeByServiceIdIfExist(serviceId: string, removingCount: number): Promise<void> {
        const renderedServiceList = this.getRenderedServiceList();

        const filtered = renderedServiceList.getFilteredValues({
            statuses: [RenderedServiceStatus.Unpaid],
            serviceIds: [serviceId],
        });

        const [removable] = filtered;

        if (removable) {
            const { groupId, count } = removable;
            await this.remove(groupId, removingCount > count ? count : removingCount);
        }
    }

    public getByMedicalCaseId(medicalCaseId: string, filterByStatus?: RenderedServiceStatus): RenderedServiceData[] {
        const renderedServiceList = this.getRenderedServiceList();
        return renderedServiceList.values.filter(({ status }) => (filterByStatus ? status === filterByStatus : true));
    }

    public async calculateRenderedServices(
        { groupId, doctorDiscount, doctorFlatDiscount, executorId, ...params }: any,
        withoutUpdate?: boolean,
    ) {
        const renderedServiceList = this.getRenderedServiceList();

        const { count, plans, serviceId } = params;
        const renderedService = renderedServiceList.getExisting(groupId);

        const { organizationId, doctorId, authorId, creationTime } = store.entry;
        const legalEntityId = store.legalEntityBranchList.getActualOnDayLegalEntityId(organizationId, creationTime);

        const costResponse = (await EntryApi.calculateRenderedService({
            count,
            serviceId,
            plans,
            organizationId,
            legalEntityId,
            doctorDiscount,
            doctorFlatDiscount,
            userId: executorId ?? doctorId ?? authorId,
            actualOn: creationTime,
        })) as any;

        const { amountToPay, totalCost } = costResponse;

        if (!withoutUpdate && renderedServiceList.has(groupId)) {
            renderedServiceList.update(groupId, {
                ...renderedService,
                amountToPay,
                count,
                totalCost,
            });
        }

        return costResponse;
    }

    public localCalculateRenderedServices(params: {
        totalCost: number;
        discount?: number;
        flatDiscount?: number;
    }): number {
        const totalCost = params.totalCost;

        const percentDiscount = params.discount ?? 0;
        const flatDiscountRub = params.flatDiscount ?? 0;

        const percentDiscountKopecks = Math.round((totalCost * percentDiscount) / 100);
        const flatDiscountKopecks = Money.kopecksFromRubles(Number(flatDiscountRub)) || 0;

        const nextAmountToPay = totalCost - percentDiscountKopecks - flatDiscountKopecks;

        return Math.max(0, Math.trunc(nextAmountToPay));
    }

    public async updateStore(): Promise<void> {
        const entry = store.entry;

        const [renderedServiceList] = await Promise.all([RenderedServiceApi.getList({ entryId: entry.id })]);

        entry.setFieldValue(EntryTypeFieldType.RenderedService, renderedServiceList.renderedServices);
        serviceInformedConsentStore.addRenderedServices(renderedServiceList.renderedServices);
    }

    private getRenderedServiceList(): RenderedServiceList {
        return store.entry.getFieldValue<RenderedServiceList>(EntryTypeFieldType.RenderedService);
    }

    private async createRenderedService(createForm: RenderedServiceCreateForm): Promise<void> {
        const { entry, app } = store;
        const isRenderedServceiAuthorWillBeDoctor = await this.isEntryAuthorDoctor();

        if (isRenderedServceiAuthorWillBeDoctor) {
            await Promise.all([
                EntryApi.createRenderedService(entry.id, createForm),
                serviceService.loadByIds([createForm.renderedService.serviceId]),
                ehrsServicePlansService.loadServicePlans({
                    ehrId: entry.ehrId,
                    serviceIds: [createForm.renderedService.serviceId],
                    actualOn: entry.creationTime,
                }),
            ]);
        } else {
            app.showingPopupParams = {
                title: translate('authorMustBeDoctorText'),
                description: translate('selectDoctorAsAuthorText'),
            };
        }
    }

    private async isEntryAuthorDoctor(): Promise<boolean> {
        const { authorId, doctorId } = store.entry;
        const userIds = uniq(compact([authorId, doctorId]));
        const users = userIds.length > 0 ? await queryClient.fetchQuery(userQueryOptions.list({ ids: userIds })) : [];

        return users.some(user => isDoctor(user) || isAssistant(user));
    }

    private getServicePlanIds({ serviceId }: { serviceId: string }): string[] {
        const { entry, ehr, ehrsServicePlans } = store;

        return (
            ehrsServicePlans
                .getServicePlans({
                    serviceId,
                    ehrId: ehr.id,
                    actualOn: entry.creationTime,
                })
                ?.map(item => item.id) ?? []
        );
    }

    private async removeRenderedServices(id: string, removingIds: string[]): Promise<void> {
        const entry = store.entry;

        const renderedServiceList = entry.getFieldValue<RenderedServiceList>(EntryTypeFieldType.RenderedService);

        await EntryApi.removeRenderedService(entry.id, removingIds);
        renderedServiceList.removeItems(id, removingIds);
    }
}

export const renderedServicesService = new RenderedServicesService();
